// Components==============
import indexTheme from 'actions/theme/index';
import showTheme from 'actions/theme/show';
import destroyThemeAttachment from 'actions/theme-attachment/destroy';
import storeThemeAttachment from 'actions/theme-attachment/store';
import storeTheme from 'actions/theme/store';
import updateTheme from 'actions/theme/update';
import Layout from 'components/Layout/Layout';
import BuilderCard from 'components/UI/BuilderCard/BuilderCard';
import Button from 'components/UI/Button/Button';
import Container from 'components/UI/Container/Container';
import Dropdown from 'components/UI/Dropdown/Dropdown';
import FormDropdown from 'components/UI/Form/FormDropdown';
import Input from 'components/UI/Form/Input';
import Textarea from 'components/UI/Form/Textarea';
import ImagesUpload from 'components/UI/ImagesUpload/ImagesUpload';
import updateProject from 'actions/project/update';
import { useAlert } from 'hooks/useAlert';
import { useFormValues } from 'hooks/useFormValues';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import FormContext from 'utilities/FormContext';
import './ThemeBuilder.module.scss';
import { useConfirm } from 'hooks/useConfirm';
import { GlobalScanContext } from 'utilities/GlobalContext/GlobalScan';
// =========================

export default function ThemeBuilder() {
  const { themeId } = useParams() as { themeId?: string };
  const dispatch = useReduxAction();
  const translate = useTranslate();
  const history = useHistory();
  const query = useMediaQ('min', 1024);

  const value = useContext(GlobalScanContext);
  const [createNewProject, setCreateNewProject] = useState(false);

  useEffect(() => {
    if (themeId) {
      dispatch(showTheme(themeId));
    }
  }, []);

  const theme = useRedux((state) =>
    themeId ? state.theme?.entities.byId[themeId] : undefined
  );

  const categoryItems = useRedux((state) =>
    state.category?.entities.allIds.map(
      (id) => state.category?.entities.byId[id]
    )
  );

  const loading = useRedux((state) => state.theme?.loading);

  const {
    formValues,
    handleChange,
    errors,
    setErrors,
    setFormValues,
  } = useFormValues({
    name: '',
    categories: [0],
    description: '',
    youtube_link: '',
  });
  const alert = useAlert();
  const confirm = useConfirm();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const [rawImages, setRawImages] = useState<any>(theme?.attachments || []);

  const [images, setImages] = useState(
    theme?.attachments.map((a) => {
      const data = JSON.parse(a?.attachment_data) as {
        file_name: string;
        file_path: string;
      };

      return { name: data.file_name, path: data.file_path, evidenceId: a.id };
    }) || []
  );

  useEffect(() => {
    if (images.length) setErrors((prev: any) => ({ ...prev, categories: [] }));
  }, [images.length]);

  useEffect(() => {
    if (selectedItems.length) {
      setErrors((prev: any) => ({ ...prev, categories: [] }));
      setFormValues((prev) => ({
        ...prev,
        categories: selectedItems.map(
          (item) => categoryItems?.find((cat) => cat?.name === item)?.id!
        ),
      }));
    }
  }, [selectedItems.length]);

  useEffect(() => {
    const name = theme?.name || '';
    const categories = theme?.categories || [];
    const description = theme?.description || '';
    const youtube_link = theme?.youtube_link || '';

    if (theme) {
      setSelectedItems(
        categories.map(
          (catId) => categoryItems?.find((cat) => cat?.id === catId)?.name || ''
        )
      );
      setFormValues((prev) => ({
        ...prev,
        name,
        categories,
        description,
        youtube_link,
      }));
    }
  }, []);

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];

    const imageReader = new FileReader();

    const evidenceId = Math.floor(Math.random() * 10e6);

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setImages((prev) => [
          ...prev,
          {
            name: file?.name || '',
            path: imageReader.result as string,
            evidenceId,
          },
        ]);

        setRawImages((prev: any) => [
          ...prev,
          {
            id: evidenceId,
            name: file?.name || '',
            path: imageReader.result as string,
            file,
            _changed: true,
          },
        ]);
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function getHeaderImagePromises(theme_id: number) {
    const prevAttachments = theme?.attachments || [];

    const attachmentIds = rawImages?.map((a: any) => a.id);
    const prevAttachmentIds = prevAttachments.map((a: any) => a.id);

    const deletePromises = prevAttachmentIds
      ?.filter((attachmentId) => !attachmentIds?.includes(attachmentId))
      .map((attachmentId) =>
        dispatch(
          destroyThemeAttachment({ attachmentId, themeId: theme_id || themeId })
        )
      );

    const createPromises = rawImages
      ?.filter((a: any) => !prevAttachmentIds.includes(a.id))
      .filter((a: any) => a._changed)
      .map(({ file }: { file: any }) =>
        dispatch(
          storeThemeAttachment({
            themeId: theme_id || themeId,
            order_index: 0,
            file,
            attachment_type: 'image',
          })
        )
      );

    return [...deletePromises, ...createPromises];
  }

  function handleDeleteImage({
    path,
    evidenceId,
  }: {
    path: string;
    evidenceId: number;
  }) {
    confirm(translate('course.confirm.delete'), () => {
      setImages((prev) => prev.filter((e) => e.path !== path));
      setRawImages((prev: any) => prev.filter((e: any) => e.id !== evidenceId));
    });
  }

  // Clear scan info from ScanContext
  function clearScanContext() {
    value.setImage(undefined);
    value.setThemeId(0);
    value.setProjectId(0);
    value.setImageTaken(false);
  }

  // Create project from image in ScanContext
  function createNewProjectFromImage() {
    console.log('koppel nieuw thema');
    console.log(value.themeId);
    dispatch(
      updateProject({
        projectId: value.projectId,
        themeId: value.themeId,
        newTheme: true,
      })
    )
      .then((project: ProjectProjectEntity) => {
        console.log(project);
        clearScanContext();
        value.setSaveIsEditable(true);
        history.push(`/werkplaats/project-details/${project.id}`);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  useEffect(() => {
    if (createNewProject) {
      createNewProjectFromImage();
      setCreateNewProject(false);
    }
  }, [createNewProject]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!selectedItems.length)
      return setErrors((prev: any) => ({
        ...prev,
        categories: [translate('project.errors.fill-all-fields')],
      }));

    if (!images.length)
      return setErrors((prev: any) => ({
        ...prev,
        categories: [translate('project.errors.fill-all-fields')],
      }));

    const saveMethod = themeId ? updateTheme : storeTheme;

    dispatch(
      saveMethod({
        id: themeId,
        onlyTheme: false,
        ...formValues,
        categories: formValues?.categories.map((c) => ({
          id: c,
        })),
      })
    )
      .then((res: { id: number; projects: ProjectEntity[] }) => {
        console.log(res);
        const theme_id = res.id;
        const projectId = res.projects?.length ? res.projects[0].id : null;
        value.setThemeId(theme_id);
        console.log(theme_id);

        return Promise.all([getHeaderImagePromises(theme_id)]).then(() =>
          Promise.resolve(projectId)
        );
      })
      .then((projectId: number) => {
        dispatch(indexTheme()).then(() => {
          if (value.imageTaken) {
            // If image already taken and in ScanContext, then scan image and couple with current theme
            setCreateNewProject(true);
          } else {
            history.push(`/werkplaats/project-details/${projectId}`);
          }
        });
      })
      .catch(() => {
        alert(translate('project.errors.saving-failed'));
      });
  }

  return (
    <Layout grey>
      <Container>
        <form styleName="outer-wrapper" onSubmit={handleSubmit}>
          <BuilderCard
            title={translate('project.actions.create-theme')}
            loading={loading}
          >
            <FormContext
              onChange={handleChange}
              errors={errors}
              formValues={formValues}
              setFormValues={setFormValues}
            >
              <div styleName="wrapper">
                <div>
                  <h2>
                    {translate('general.terms.title')} <span>*</span>
                  </h2>
                  <p styleName="text">
                    {translate('project.inputs.theme-title')}
                  </p>
                  <Input
                    color="grey"
                    name="name"
                    placeholder={translate('general.terms.title')}
                  />
                </div>
                <div>
                  <h2>
                    {translate('general.terms.theme-description')}{' '}
                    <span>*</span>
                  </h2>
                  <p styleName="text">
                    {translate('project.inputs.theme-description')}{' '}
                    <NavLink to="/bibliotheek/groowtorials">
                      {translate('general.terms.explanation')}
                    </NavLink>
                  </p>
                  <Textarea
                    color="grey"
                    name="description"
                    placeholder={translate('general.terms.theme-description')}
                  />
                </div>
                <div>
                  <h2>
                    {translate('general.terms.imagery')} <span>*</span>
                  </h2>
                  <p styleName="text">
                    {translate('project.inputs.theme-imagery')}
                  </p>
                  <div styleName="upload-wrapper">
                    <ImagesUpload
                      images={images}
                      handleUpload={handleUpload}
                      handleDelete={handleDeleteImage}
                    />
                  </div>
                  <Input
                    color="grey"
                    name="youtube_link"
                    placeholder="Youtube link"
                    required={false}
                  />
                </div>
                <div>
                  <h2>
                    {translate('general.terms.categories')} <span>*</span>
                  </h2>
                  <p styleName="text">
                    {translate('project.inputs.theme-categories')}
                  </p>
                  <FormDropdown
                    name="categories"
                    dropdown={
                      <Dropdown
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                        initialOptions={
                          categoryItems?.map((cat) => cat?.name || '') || []
                        }
                        initialText={translate(
                          'filters.category-filter.select'
                        )}
                        color="grey"
                      />
                    }
                  />
                </div>
              </div>
            </FormContext>
          </BuilderCard>
          <Button inactive={loading} color="grey">
            {translate('general.terms.save')}
          </Button>
        </form>
      </Container>
    </Layout>
  );
}
